import { FC, useEffect, useRef, useState } from 'react'
import DOMPurify from 'dompurify'
import { getAtendimentoById } from '../../atendimento/core/_requests'

import { AtendimentoView, initialAtendimento } from '../../atendimento/core/_models'
import { VideoAtendimento } from './video-atendimento'
import { LISTA_ATENDIMENTOS, rotaParecerInterconsulta } from '../parecer-interconsulta/core/rotas'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ListLoading } from '../../../../components'
import {
  AnotacaoType,
  AtendimentoType,
  showAlert,
  showError,
} from '../../../../../_metronic/helpers'
import { useSessaoAtendimento } from '../chamada-ativa/useSessaoAtendimento'
import { videoAgoraHabilitado } from '../chamada-ativa/core/_models'

type Props = {
  /** Rota do profissional: a chamada sobrevive à navegação (LO-829). */
  chamadaPersistente?: boolean
  /** Para onde voltar quando o atendimento não carrega (sem acesso, inexistente). */
  rotaLista?: string
}

/**
 * Trocar só o `?atendimento=` mantém a mesma rota e não remontaria a tela (o atendimento é carregado no
 * mount). A `key` pelo id garante que o link "Voltar à chamada em andamento" e a barra do mini-player
 * levem de fato ao atendimento da chamada (LO-829).
 */
export const TeleAtendimento: FC<Props> = (props) => {
  const [searchParams] = useSearchParams()
  return <TeleAtendimentoDoAtendimento key={searchParams.get('atendimento') ?? ''} {...props} />
}

const TeleAtendimentoDoAtendimento: FC<Props> = ({
  chamadaPersistente = false,
  rotaLista = LISTA_ATENDIMENTOS,
}) => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [atendimento, setAtendimento] = useState<AtendimentoView>(initialAtendimento)

  const idAtendimento = String(searchParams.get('atendimento'))
  const persistente = chamadaPersistente && videoAgoraHabilitado()
  const { observacoesExibidas, atualizar: atualizarSessao } = useSessaoAtendimento(
    Number(idAtendimento),
    persistente
  )
  // A busca roda uma vez por atendimento: lê a sessão mais recente sem reexecutar quando ela muda
  // (marcar `observacoesExibidas` não pode disparar um novo fetch).
  const observacoesExibidasRef = useRef(observacoesExibidas)
  observacoesExibidasRef.current = observacoesExibidas
  const atualizarSessaoRef = useRef(atualizarSessao)
  atualizarSessaoRef.current = atualizarSessao

  useEffect(() => {
    const fetchData = async () => {
      try {
        const atendimento = await getAtendimentoById(Number(idAtendimento))

        // Interconsulta não passa por esta tela: o parecer tem tela própria. URL aberta direto
        // (link antigo, histórico do navegador) vai para lá.
        if (atendimento.tipo === AtendimentoType.Interconsulta) {
          navigate(rotaParecerInterconsulta(atendimento.id as number), { replace: true })
          return
        }

        setAtendimento(atendimento)

        const observacoes = atendimento?.anotacoes?.filter(
          (anotacao) => anotacao.tipo === AnotacaoType.Observacao
        )

        // Com a chamada persistente o médico sai e volta à tela: o alerta aparece uma vez por chamada.
        if (observacoes && observacoes.length > 0 && !observacoesExibidasRef.current) {
          const html = observacoes
            .map((o) => {
              // Texto livre vindo da API vira HTML do alerta (innerHTML do SweetAlert): sanitizar com lista
              // mínima de tags, como manda a regra de HTML dinâmico do web/CLAUDE.md.
              const anotacaoFormatada = DOMPurify.sanitize(
                (o.anotacao ?? '').replace(/\n/g, '<br/>'),
                { ALLOWED_TAGS: ['br', 'b', 'i'], ALLOWED_ATTR: [] }
              )
              return `
        <div style="
          margin-bottom:8px;
          padding:6px 10px;
          border-left:3px solid var(--bs-info-active);
          background-color: var(--bs-light);
          border-radius:4px;
          color: var(--bs-body-color);
        ">
          ${anotacaoFormatada}
        </div>
      `
            })
            .join('')

          // Texto livre de anotação vai para HTML: sanitizar.
          showAlert({ title: 'Observações', html: DOMPurify.sanitize(html), icon: 'info' })
          atualizarSessaoRef.current({ observacoesExibidas: true })
        }
        setIsLoading(false)
      } catch (error) {
        // Sem o atendimento não há o que mostrar: depois do alerta, volta para a lista em vez de
        // deixar o carregamento girando.
        await showError(error)
        navigate(rotaLista, { replace: true })
      }
    }

    fetchData()
  }, [idAtendimento, navigate, rotaLista])

  return (
    <>
      {isLoading ? (
        <ListLoading />
      ) : (
        <VideoAtendimento atendimento={atendimento} chamadaPersistente={persistente} />
      )}
    </>
  )
}
